import React, { useState } from "react";

import PlusCircleIcon from "@uui/PlusCircle";
import { filter, map, isEmpty } from "lodash/fp";

import { Typography } from "@mui/material";
import styled from "@mui/material/styles/styled";

import SettingsTitle from "@app/analytics/account/components/SettingsTitle";
import Tag from "@app/analytics/components/Tag";
import AddCustomTagPopover from "@app/analytics/components/TagPopover/AddCustomTagPopover";
import EditCustomTagPopover from "@app/analytics/components/TagPopover/EditCustomTagPopover";
import useTags from "@app/common/hooks/tags/useTags";
import { showToast } from "@app/common/hooks/useToasts";
import type { Tag as ITag } from "@app/common/types";
import { CreateCustomTagBody } from "@app/common/types";

import Button from "@rui/Button";
import ConfirmationModal from "@rui/ConfirmationModal";
import { Column, Row } from "@rui/Stack";
import { Bold } from "@rui/styles";

import WorkspaceSettingsAccordion from "../components/WorkspaceSettingsAccordion";

const TagList = styled(Row)({
  rowGap: "6px",
  columnGap: "6px",
  flexWrap: "wrap",
});

const TagListWrapper = styled(Column)(({ theme }) => ({
  borderRadius: "4px",
  padding: theme.spacing(1),
  rowGap: "12px",
  border: `1px solid ${theme.palette.shades.neutral[100]}`,
  minWidth: "30%",
}));

interface WorkspaceTagsModuleProps {
  workspaceId: string;
  workspaceIsInactive: boolean;
}

const WorkspaceTagsModule = ({
  workspaceId,
  workspaceIsInactive,
}: WorkspaceTagsModuleProps) => {
  const { tags, createTags, deleteTag, updateTag } = useTags();

  const workspaceTags = filter("workspaceId", tags);

  const [tagToConfirmDelete, setTagToConfirmDelete] = useState<ITag | null>(
    null
  );

  const handleCreateTags = (newTags: CreateCustomTagBody[]) => {
    const workspaceCreateTags = map(
      (tag) => ({ ...tag, workspaceId }),
      newTags
    );
    createTags.mutate(workspaceCreateTags, {
      onError: () =>
        showToast({
          severity: "error",
          message:
            "Oops! Something went wrong and some tags could not be saved.",
        }),
    });
  };

  const handleSaveTag = (updatedTag: ITag) => {
    updateTag.mutate(updatedTag, {
      onError: () =>
        showToast({
          severity: "error",
          message: "Oops! Something went wrong and the tag could not be saved.",
        }),
    });
  };

  const resetTagToDelete = () => setTagToConfirmDelete(null);

  const handleDeleteTag = (tagToDelete: ITag) => {
    setTagToConfirmDelete(tagToDelete);
  };

  const handleConfirmDelete = () => {
    if (!tagToConfirmDelete) return null;

    deleteTag.mutate(tagToConfirmDelete.id, {
      onError: () =>
        showToast({
          severity: "error",
          message:
            "Oops! Something went wrong and your tag could not be deleted.",
        }),
      onSettled: resetTagToDelete,
    });
  };

  return (
    <WorkspaceSettingsAccordion
      title="Tagging"
      subtitle="Add meeting tags accessible to all Workspace members"
    >
      <Column variant="left" spacing="12px">
        <Row variant="apart" minHeight="42px">
          <SettingsTitle
            title="Workspace Custom Tags"
            subtitle="Manage custom meeting tags for the Workspace. These tags are visible to and can be used by all Workspace members."
          />
        </Row>
        {!isEmpty(workspaceTags) && (
          <TagListWrapper>
            <TagList>
              {workspaceTags.map((tag) => (
                <EditCustomTagPopover
                  key={tag.id}
                  allTags={tags}
                  onSave={handleSaveTag}
                  renderControl={(props) => (
                    <Tag
                      {...props}
                      sizing="padded"
                      onAction={handleDeleteTag}
                    />
                  )}
                  tag={tag}
                />
              ))}
            </TagList>
          </TagListWrapper>
        )}
        <AddCustomTagPopover
          title="Create Workspace Custom Tags"
          allTags={tags}
          onSave={handleCreateTags}
          renderControl={(props) => (
            <Button
              {...props}
              sx={{ borderRadius: "6px", height: "40px" }}
              disabled={workspaceIsInactive}
              startIcon={<PlusCircleIcon sx={{ width: "16px" }} />}
            >
              Add new
            </Button>
          )}
        />
        <ConfirmationModal
          title="Delete tag?"
          open={tagToConfirmDelete != null}
          onClose={resetTagToDelete}
          onConfirm={handleConfirmDelete}
          size="md"
          confirmProps={{
            type: "negative",
            label: "Delete tag",
          }}
          loading={deleteTag.isPending}
        >
          <Typography sx={{ color: "shades.neutral.600", lineHeight: 20 / 16 }}>
            When you delete your custom tag "
            <Bold>{tagToConfirmDelete?.name}</Bold>", it will be removed from
            all meeting reports that are assigned that tag.
          </Typography>
        </ConfirmationModal>
      </Column>
    </WorkspaceSettingsAccordion>
  );
};

export default WorkspaceTagsModule;
